import React from "react"
import { injectIntl, defineMessages } from "react-intl"
import { Intl } from "../../../state/helpers/IntlProps"
import EcommerceStyleMapping from "../../../state/ecommerce-configuration/EcommerceStyleMapping"
import Select from "react-select"
import {
    CART_TO_USE,
    CART_TO_USE_OPTIONS,
    BUTTON_POSITION,
    BUTTON_POSITION_OPTIONS,
    INPUT_LABEL_POSITION,
    INPUT_LABEL_POSITION_OPTIONS,
    OPEN_CART_ONCLICK_OPTION,
    OPEN_CART_ONCLICK_OPTIONS,
    STYLE_INPUT_MAPPING,
    ECOMMERCE_CONFIGURATION_TYPE
} from "../../../state/ecommerce-configuration/EcommerceTypes"
import ColorPicker from "../../../common/color/picker.component"
import UseShowAdvancedToggle from "../../../common/hooks/UseShowAdvancedToggle"

type OwnProps = {
    intl: Intl
    styleMapping: EcommerceStyleMapping
    onChangeCallback: (key: string, value: string) => void
}


const positionLabel = defineMessages({
    [BUTTON_POSITION.TOP_RIGHT]: {
        id: "ecommerce.components.ecommerce_style_input.floating_button_position_top_right",
        defaultMessage: "Top Right",
    },
    [BUTTON_POSITION.TOP_LEFT]: {
        id: "ecommerce.components.ecommerce_style_input.floating_button_position_top_left",
        defaultMessage: "Top Left",
    },
    [BUTTON_POSITION.MID_RIGHT]: {
        id: "ecommerce.components.ecommerce_style_input.floating_button_position_mid_right",
        defaultMessage: "Middle Right",
    },
    [BUTTON_POSITION.MID_LEFT]: {
        id: "ecommerce.components.ecommerce_style_input.floating_button_position_mid_left",
        defaultMessage: "Middle Left",
    },
    [BUTTON_POSITION.BOTTOM_RIGHT]: {
        id: "ecommerce.components.ecommerce_style_input.floating_button_position_bottom_right",
        defaultMessage: "Bottom Right",
    },
    [BUTTON_POSITION.BOTTOM_LEFT]: {
        id: "ecommerce.components.ecommerce_style_input.floating_button_position_bottom_left",
        defaultMessage: "Bottom Left",
    },
})

const cartLabel = defineMessages({
    [CART_TO_USE.MAIN_CART]: {
        id: "ecommerce.components.ecommerce_style_input.cart_to_display.main_cart",
        defaultMessage: "Main Cart",
    },
    [CART_TO_USE.QUOTE_CART]: {
        id: "ecommerce.components.ecommerce_style_input.cart_to_display.quote_cart",
        defaultMessage: "Quote Cart",
    },
})

const openCartOnClickOptionLabel = defineMessages({
    [OPEN_CART_ONCLICK_OPTION.PRODUCT_PAGE_ONLY]: {
        id: "ecommerce.components.ecommerce_style_input.open_cart_onclick_option_product_page",
        defaultMessage: "Only on product page",
    },
    [OPEN_CART_ONCLICK_OPTION.COLLECTION_PAGE_ONLY]: {
        id: "ecommerce.components.ecommerce_style_input.open_cart_onclick_option_collection_page",
        defaultMessage: "Only on collection page",
    },
    [OPEN_CART_ONCLICK_OPTION.NEVER]: {
        id: "ecommerce.components.ecommerce_style_input.open_cart_onclick_option_never",
        defaultMessage: "Never",
    },
    [OPEN_CART_ONCLICK_OPTION.ALWAYS]: {
        id: "ecommerce.components.ecommerce_style_input.open_cart_onclick_option_always",
        defaultMessage: "Always",
    },
})

const inputPositionLabel = defineMessages({
    [INPUT_LABEL_POSITION.TOP_POSITION]: {
        id: "ecommerce.components.ecommerce_style_input.input_position.top_position",
        defaultMessage: "Top",
    },
    [INPUT_LABEL_POSITION.BOTTOM_POSITION]: {
        id: "ecommerce.components.ecommerce_style_input.input_position.bottom_position",
        defaultMessage: "Bottom",
    },
    [INPUT_LABEL_POSITION.HIDDEN_POSITION]: {
        id: "ecommerce.components.ecommerce_style_input.input_position.hidden_position",
        defaultMessage: "Hidden",
    },
})

const advancedKeyValues = ["class", "style"]
const hiddenKeyValues = ["cartToUse"]

const labelMessage = defineMessages({
    "containerClass": {
        id: "ecommerce.components.ecommerce_style_input.container_label",
        defaultMessage: "Container Class",
    },
    "rowHeaderClass": {
        id: "ecommerce.components.ecommerce_style_input.row_header_label",
        defaultMessage: "Row Header Class",
    },
    "colLeftClass": {
        id: "ecommerce.components.ecommerce_style_input.col_left_label",
        defaultMessage: "Col Left Class",
    },
    "colClass": {
        id: "ecommerce.components.ecommerce_style_input.col_label",
        defaultMessage: "Col Class",
    },
    "colRightClass": {
        id: "ecommerce.components.ecommerce_style_input.col_right_label",
        defaultMessage: "Col Right Class",
    },
    "rowClass": {
        id: "ecommerce.components.ecommerce_style_input.ow_label",
        defaultMessage: "Row Class",
    },
    "colFooterClass": {
        id: "ecommerce.components.ecommerce_style_input.col_footer_label",
        defaultMessage: "Col Footer Class",
    },
    "contentClass": {
        id: "ecommerce.components.ecommerce_style_input.content_label",
        defaultMessage: "Content Class",
    },
    "cartToUse": {
        id: "ecommerce.components.ecommerce_style_input.cart_to_use",
        defaultMessage: "Cart To Use",
    },
    "buttonColor": {
        id: "ecommerce.components.ecommerce_style_input.button_color",
        defaultMessage: "Button Color",
    },
    "buttonLabel": {
        id: "ecommerce.components.ecommerce_style_input.button_Label",
        defaultMessage: "Button Label",
    },
    "buttonPosition": {
        id: "ecommerce.components.ecommerce_style_input.button_position",
        defaultMessage: "Button Position",
    },
    "modalTitle": {
        id: "ecommerce.components.ecommerce_style_input.modal_title",
        defaultMessage: "Modal Title",
    },
    "buttonLabelColor": {
        id: "ecommerce.components.ecommerce_style_input.button_label_color",
        defaultMessage: "Button Label Color",
    },
    "buttonStyle": {
        id: "ecommerce.components.ecommerce_style_input.button_style",
        defaultMessage: "Button Style",
    },
    "buttonClass": {
        id: "ecommerce.components.ecommerce_style_input.button_class",
        defaultMessage: "Button Class",
    },
    "openCartOnClickOption": {
        id: "ecommerce.components.ecommerce_style_input.open_cart_on_click_option",
        defaultMessage: "Open Cart On Click",
    },
    "groupClass": {
        id: "ecommerce.components.ecommerce_style_input.group_class",
        defaultMessage: "Input Group Class",
    },
    "boxClass": {
        id: "ecommerce.components.ecommerce_style_input.box_class",
        defaultMessage: "Input Box Class",
    },
    "labelClass": {
        id: "ecommerce.components.ecommerce_style_input.label_class",
        defaultMessage: "Input Label Class",
    },
    "labelPosition": {
        id: "ecommerce.components.ecommerce_style_input.label_position",
        defaultMessage: "Input Label Position",
    },
    "thankYouMessage": {
        id: "ecommerce.components.ecommerce_style_input.thank_you_message",
        defaultMessage: "Thank You Message",
    },
})

const defaultPlaceholderMessages = defineMessages({
    "continue" : {
        id: "ecommerce.components.ecommerce_style_input.continue",
        defaultMessage: "Continue",
    },
})

const EcommerceStylesMappingInputs = (props: OwnProps) => {
    if (!props.styleMapping?.getAttributes()) return <></>
    const {toggleContent, toggleCurrent} = UseShowAdvancedToggle()

    const getDefaultValuePlaceholder = (key: string) => {
        switch (key) {
        case "buttonLabel":
            if (props.styleMapping.getType() === ECOMMERCE_CONFIGURATION_TYPE.QUOTE_CART_STYLE) {
                return props.intl.formatMessage(defaultPlaceholderMessages.continue)
            }
            return ""
        default:
            return ""
        }
    }

    const getOptions = (key: string) => {
        switch (key) {
        case "buttonPosition":
            return BUTTON_POSITION_OPTIONS.map(item => {
                return {
                    value: item, label: props.intl.formatMessage(positionLabel[item])
                }
            })
        case "cartToUse":
            return CART_TO_USE_OPTIONS.map(item => {
                return {
                    value: item, label: props.intl.formatMessage(cartLabel[item])
                }
            })
        case "openCartOnClickOption":
            return OPEN_CART_ONCLICK_OPTIONS.map(item => {
                return {
                    value: item, label: props.intl.formatMessage(openCartOnClickOptionLabel[item])
                }
            })
        case "labelPosition":
            return INPUT_LABEL_POSITION_OPTIONS.map(item => {
                return {
                    value: item, label: props.intl.formatMessage(inputPositionLabel[item])
                }
            })
        }
    }

    const getInputType = (key) => {
        switch (STYLE_INPUT_MAPPING[key]) {
        case "color":
            return <div>
                <ColorPicker
                    className={"mr-2"}
                    value={props.styleMapping.getAttributes()[key]}
                    onChange={c => {
                        props.onChangeCallback(key, c)
                    }}
                />
            </div>
        case "select":
            return <div>
                <Select
                    options={getOptions(key)}
                    value={props.styleMapping.getAttributes()[key]}
                    onChange={choice => {
                        props.onChangeCallback(key, choice.value)
                    }}
                    clearable={false}>
                </Select>
            </div>
        default:
            return <div className="input-group">
                <input
                    name={key}
                    id={key}
                    type="text"
                    placeholder={getDefaultValuePlaceholder(key)}
                    className="form-control"
                    value={props.styleMapping.getAttributes()[key]}
                    onChange={(e) => {
                        props.onChangeCallback(key, e.target.value)
                    }}
                />
            </div>
        }
    }

    const getRow = (key) => {
        return <div className="form-group row align-items-center">
            <label className="col-2 col-form-label text-align-right" htmlFor={key}>
                {props.intl.formatMessage(labelMessage[key])}
            </label>

            <div className="col">
                {getInputType(key)}
            </div>
        </div>
    }

    const baseAttributes: string[] = []
    const advancedAttributes: string[] = []

    Object.keys(props.styleMapping.getAttributes()).forEach(key => {
        if (hiddenKeyValues.includes(key)) return

        if (advancedKeyValues.filter(v => {return key.toLocaleLowerCase().includes(v)}).length > 0) {
            advancedAttributes.push(key)
            return
        }

        baseAttributes.push(key)
    }
    )

    return (
        <div>
            <div>
                {baseAttributes.map(key => getRow(key))}

                <div hidden={!toggleCurrent}>
                    {advancedAttributes.map(key => getRow(key))}
                </div>

                {advancedAttributes.length > 0 &&
                    <div className="text-center pt-3">
                        {toggleContent}
                    </div>
                }
            </div>
        </div>
    )

}

export default injectIntl(EcommerceStylesMappingInputs)


// WEBPACK FOOTER //
// ./src/settings/ecommerce/components/EcommerceStyleInputListComponent.tsx